import config from "@/config/config";
import {
  Exercise,
  ExerciseData,
} from "./exercises.interfaces";
import {
  Exercise as ComponentExercise,
} from "@/components/exercises/exercise.interfaces";
import {
  createExerciseMock,
  updateExerciseMock,
  deleteExerciseMock,
  listExercisesByModuleMock,
  listExercisesByLevelTestGroupMock,
  createExerciseForLevelTestGroupMock,
  updateExerciseForLevelTestGroupMock,
  deleteExerciseForLevelTestGroupMock,
} from "./exercises.mocks";
import {
  apiCreateExercise,
  apiUpdateExercise,
  apiDeleteExercise,
  apiListExercisesByModule,
  apiUpdateExercisesOrder,
  apiListExercisesByLevelTestGroup,
  apiCreateExerciseForLevelTestGroup,
  apiUpdateExerciseForLevelTestGroup,
  apiDeleteExerciseForLevelTestGroup,
} from "./exercises.api";

// Mock implementation for listing exercises by module
const listExercisesByModuleMockWrapper = async (
  moduleId: string
): Promise<Exercise[]> => {
  return listExercisesByModuleMock(moduleId);
};

// Mock implementation for updateExercisesOrder
const updateExercisesOrderMock = async (
  moduleId: string,
  exerciseIds: (string | number)[]
): Promise<void> => {
  console.log("Mock: Atualizando ordem dos exercícios", moduleId, exerciseIds);
  // Simulando sucesso
  return Promise.resolve();
};

// Exported service functions that use either real or mock implementations
export const createExercise = config.selectApi(
  apiCreateExercise,
  createExerciseMock
);
export const updateExercise = config.selectApi(
  apiUpdateExercise,
  updateExerciseMock
);
export const deleteExercise = config.selectApi(
  apiDeleteExercise,
  deleteExerciseMock
);
export const listExercisesByModule = config.selectApi(
  apiListExercisesByModule,
  listExercisesByModuleMockWrapper
);
export const updateExercisesOrder = config.selectApi(
  apiUpdateExercisesOrder,
  updateExercisesOrderMock
);

// Helper function for safe JSON parsing
const safeParseExerciseData = (data: any): ExerciseData | null => {
  try {
    if (typeof data === 'string') {
      return JSON.parse(data);
    }
    return data;
  } catch (error) {
    console.error('Error parsing exercise data:', error);
    return null;
  }
};

// Helper function for safe JSON stringifying  
const safeStringifyExerciseData = (data: ExerciseData): string => {
  try {
    return JSON.stringify(data);
  } catch (error) {
    console.error('Error stringifying exercise data:', error);
    return '{}';
  }
};

// Exported service object
export const exerciseService = {
  // No more conversion functions needed - using direct JSON handling

  // CRUD operations with simplified JSON parsing
  async createComponentExercise(
    moduleId: string,
    exercise: ComponentExercise
  ): Promise<ComponentExercise> {
    const result = await createExercise({
      moduleId: moduleId,
      type: exercise.type as any, // Type compatibility between admin and component interfaces
      data: exercise.data as any, // API will stringify this internally
      order: (exercise as any).order || 0,
    });
    
    // Parse data back for component consumption
    return { 
      ...result, 
      data: safeParseExerciseData(result.data),
      type: result.type as any // Type compatibility
    } as ComponentExercise;
  },

  async updateComponentExercise(
    exerciseId: string,
    exercise: ComponentExercise,
    moduleId?: string
  ): Promise<ComponentExercise> {
    if (!moduleId && exercise.id && typeof exercise.id === "string") {
      moduleId = exercise.id.split("_")[0]; // Extrair moduleId do ID do exercício se não fornecido
    }

    const result = await updateExercise(exerciseId, {
      type: exercise.type as any, // Type compatibility between admin and component interfaces
      data: exercise.data as any, // API will stringify this internally
      moduleId: moduleId || "",
      order: (exercise as any).order ?? 0, // Changed from || to ?? to allow 0 but mainly to be explicit
      isActive: exercise.isActive !== false,
    });
    
    // Parse data back for component consumption
    return { 
      ...result, 
      data: safeParseExerciseData(result.data),
      type: result.type as any // Type compatibility
    } as ComponentExercise;
  },

  async deleteComponentExercise(
    exerciseId: string,
    moduleId?: string
  ): Promise<void> {
    if (!moduleId && exerciseId && typeof exerciseId === "string") {
      moduleId = exerciseId.split("_")[0]; // Extrair moduleId do ID do exercício se não fornecido
    }

    if (!moduleId) {
      throw new Error("Module ID is required for deleting exercises");
    }

    await deleteExercise(exerciseId, moduleId);
  },

  async listComponentExercisesByModule(
    moduleId: string
  ): Promise<ComponentExercise[]> {
    const exercises = await listExercisesByModule(moduleId);
    
    // Parse data for each exercise if it comes as string from API
    return exercises.map(ex => ({
      ...ex,
      data: safeParseExerciseData(ex.data),
      type: ex.type as any // Type compatibility between admin and component interfaces
    })) as ComponentExercise[];
  },

  // Level Test Group Exercises
  async listComponentExercisesByLevelTestGroup(
    levelTestGroupId: string
  ): Promise<ComponentExercise[]> {
    console.log('🔍 listComponentExercisesByLevelTestGroup - useRealApi:', config.useRealApi);
    console.log('🔍 API URL will be:', `${config.apiBaseUrl}/admin/level-test-groups/${levelTestGroupId}/exercises`);
    
    const exercises = await (config.useRealApi ? 
      apiListExercisesByLevelTestGroup(levelTestGroupId) : 
      listExercisesByLevelTestGroupMock(levelTestGroupId)
    );
    
    return exercises.map(ex => ({
      ...ex,
      data: safeParseExerciseData(ex.data),
      type: ex.type as any
    })) as ComponentExercise[];
  },

  async createComponentExerciseForLevelTestGroup(
    levelTestGroupId: string,
    exercise: ComponentExercise
  ): Promise<ComponentExercise> {
    const result = await (config.useRealApi ? 
      apiCreateExerciseForLevelTestGroup({
        levelTestGroupId: levelTestGroupId,
        type: exercise.type as any,
        data: exercise.data as any,
        order: (exercise as any).order || 0,
      }) :
      createExerciseForLevelTestGroupMock({
        levelTestGroupId: levelTestGroupId,
        type: exercise.type as any,
        data: exercise.data as any,
        order: (exercise as any).order || 0,
      })
    );
    
    return { 
      ...result, 
      data: safeParseExerciseData(result.data),
      type: result.type as any
    } as ComponentExercise;
  },

  async updateComponentExerciseForLevelTestGroup(
    exerciseId: string,
    exercise: ComponentExercise,
    levelTestGroupId?: string
  ): Promise<ComponentExercise> {
    const result = await (config.useRealApi ?
      apiUpdateExerciseForLevelTestGroup(exerciseId, {
        type: exercise.type as any,
        data: exercise.data as any,
        levelTestGroupId: levelTestGroupId || "",
        order: (exercise as any).order || 0,
      }) :
      updateExerciseForLevelTestGroupMock(exerciseId, {
        type: exercise.type as any,
        data: exercise.data as any,
        levelTestGroupId: levelTestGroupId || "",
        order: (exercise as any).order || 0,
      })
    );
    
    return { 
      ...result, 
      data: safeParseExerciseData(result.data),
      type: result.type as any
    } as ComponentExercise;
  },

  async deleteComponentExerciseForLevelTestGroup(
    exerciseId: string,
    levelTestGroupId?: string
  ): Promise<void> {
    if (!levelTestGroupId) {
      throw new Error("Level Test Group ID is required for deleting exercises");
    }

    await (config.useRealApi ?
      apiDeleteExerciseForLevelTestGroup(exerciseId, levelTestGroupId) :
      deleteExerciseForLevelTestGroupMock(exerciseId, levelTestGroupId)
    );
  },

  // Helper methods for JSON handling
  safeParseExerciseData,
  safeStringifyExerciseData,
};